// SNIGJ - Prison Management Page (Edit Prison Details)
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { 
  Building2, MapPin, Users, Save, ArrowLeft, Plus, Trash2, Edit,
  Home, Bath, UtensilsCrossed, Stethoscope, AlertTriangle, Settings,
  Grid3X3, Phone, Mail, Globe
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

// DRC Provinces
const DRC_PROVINCES = [
  "KINSHASA", "KONGO-CENTRAL", "KWANGO", "KWILU", "MAI-NDOMBE",
  "KASAI", "KASAI-CENTRAL", "KASAI-ORIENTAL", "LOMAMI", "SANKURU",
  "MANIEMA", "SUD-KIVU", "NORD-KIVU", "ITURI", "HAUT-UELE",
  "TSHOPO", "BAS-UELE", "NORD-UBANGI", "SUD-UBANGI", "MONGALA",
  "EQUATEUR", "TSHUAPA", "TANGANYIKA", "HAUT-LOMAMI", "LUALABA", "HAUT-KATANGA"
];

// Detention conditions rating
const DETENTION_CONDITIONS = [
  { value: 'EXCELLENT', label: { fr: 'Excellentes', en: 'Excellent' }, color: 'bg-green-500' },
  { value: 'GOOD', label: { fr: 'Bonnes', en: 'Good' }, color: 'bg-blue-500' },
  { value: 'ACCEPTABLE', label: { fr: 'Acceptables', en: 'Acceptable' }, color: 'bg-yellow-500' },
  { value: 'POOR', label: { fr: 'Mauvaises', en: 'Poor' }, color: 'bg-orange-500' },
  { value: 'CRITICAL', label: { fr: 'Critiques', en: 'Critical' }, color: 'bg-red-500' },
];

const PrisonEditPage = () => {
  const { prisonId } = useParams();
  const navigate = useNavigate();
  const { language, user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [prison, setPrison] = useState(null);
  const [activeTab, setActiveTab] = useState('general');
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    province: '',
    address: '',
    phone: '',
    email: '',
    latitude: '',
    longitude: '',
    capacity: 0,
    cells: [],
    has_infirmary: false,
    toilets_count: 0,
    has_kitchen: false,
    detention_conditions: 'ACCEPTABLE',
    notes: ''
  });
  
  // Cell editing
  const [showCellModal, setShowCellModal] = useState(false);
  const [editingCell, setEditingCell] = useState(null);
  const [cellForm, setCellForm] = useState({
    cell_id: '',
    name: '',
    pavilion: '',
    capacity: 10,
    cell_type: 'STANDARD',
    floor: 0,
    is_active: true
  });

  useEffect(() => {
    if (prisonId) {
      loadPrison();
    }
  }, [prisonId]);

  const loadPrison = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/prisons/${prisonId}`);
      const prisonData = response.data;
      
      setPrison(prisonData);
      setFormData({
        name: prisonData.name || '',
        province: prisonData.province || '',
        address: prisonData.address || '',
        phone: prisonData.phone || '',
        email: prisonData.email || '',
        latitude: prisonData.latitude || '',
        longitude: prisonData.longitude || '',
        capacity: prisonData.capacity || 0,
        cells: prisonData.cells || [],
        has_infirmary: prisonData.has_infirmary || false,
        toilets_count: prisonData.toilets_count || 0,
        has_kitchen: prisonData.has_kitchen || false,
        detention_conditions: prisonData.detention_conditions || 'ACCEPTABLE',
        notes: prisonData.notes || ''
      });
    } catch (error) {
      console.error('Failed to load prison:', error);
      toast.error(language === 'fr' ? 'Erreur lors du chargement' : 'Failed to load prison');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/prisons/${prisonId}`, formData);
      toast.success(language === 'fr' ? 'Prison mise à jour avec succès' : 'Prison updated successfully');
      navigate('/prisons');
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur lors de la sauvegarde' : 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  // Cell management functions
  const openCellModal = (cell = null) => {
    if (cell) {
      setEditingCell(cell);
      setCellForm({ ...cell });
    } else {
      setEditingCell(null);
      setCellForm({
        cell_id: `cell_${Date.now()}`,
        name: '',
        pavilion: '',
        capacity: 10,
        cell_type: 'STANDARD',
        floor: 0,
        is_active: true
      });
    }
    setShowCellModal(true);
  };

  const saveCell = () => {
    if (!cellForm.name) {
      toast.error(language === 'fr' ? 'Le nom de la cellule est requis' : 'Cell name is required');
      return;
    }

    const updatedCells = [...formData.cells];
    
    if (editingCell) {
      const index = updatedCells.findIndex(c => c.cell_id === editingCell.cell_id);
      if (index !== -1) {
        updatedCells[index] = cellForm;
      }
    } else {
      updatedCells.push(cellForm);
    }

    setFormData({ ...formData, cells: updatedCells });
    setShowCellModal(false);
    toast.success(language === 'fr' ? 'Cellule enregistrée' : 'Cell saved');
  };

  const deleteCell = (cellId) => {
    const updatedCells = formData.cells.filter(c => c.cell_id !== cellId);
    setFormData({ ...formData, cells: updatedCells });
    toast.success(language === 'fr' ? 'Cellule supprimée' : 'Cell deleted');
  };

  // Calculate total cell capacity
  const totalCellCapacity = formData.cells.reduce((sum, cell) => sum + (cell.capacity || 0), 0);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      </DashboardLayout>
    );
  }

  if (!prison) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <Building2 className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold mb-2">
            {language === 'fr' ? 'Prison non trouvée' : 'Prison not found'}
          </h2>
          <Button onClick={() => navigate('/prisons')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            {language === 'fr' ? 'Retour' : 'Back'}
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="prison-edit-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/prisons')}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Building2 className="w-7 h-7 text-primary" />
                {language === 'fr' ? 'Éditer la Prison' : 'Edit Prison'}
              </h1>
              <p className="text-muted-foreground">{prison.name}</p>
            </div>
          </div>
          <Button onClick={handleSave} disabled={saving} className="gap-2">
            <Save className="w-4 h-4" />
            {saving 
              ? (language === 'fr' ? 'Enregistrement...' : 'Saving...') 
              : (language === 'fr' ? 'Enregistrer' : 'Save')}
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="general" className="gap-2">
              <Settings className="w-4 h-4" />
              {language === 'fr' ? 'Général' : 'General'}
            </TabsTrigger>
            <TabsTrigger value="cells" className="gap-2">
              <Grid3X3 className="w-4 h-4" />
              {language === 'fr' ? 'Cellules' : 'Cells'} ({formData.cells.length})
            </TabsTrigger>
            <TabsTrigger value="facilities" className="gap-2">
              <Home className="w-4 h-4" />
              {language === 'fr' ? 'Installations' : 'Facilities'}
            </TabsTrigger>
            <TabsTrigger value="location" className="gap-2">
              <MapPin className="w-4 h-4" />
              {language === 'fr' ? 'Localisation' : 'Location'}
            </TabsTrigger>
          </TabsList>

          {/* General Tab */}
          <TabsContent value="general" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <CardTitle>{language === 'fr' ? 'Informations Générales' : 'General Information'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Nom de la prison' : 'Prison Name'}</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Ex: Prison Centrale de Makala"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Province' : 'Province'}</Label>
                    <Select value={formData.province} onValueChange={(v) => setFormData({ ...formData, province: v })}>
                      <SelectTrigger>
                        <SelectValue placeholder={language === 'fr' ? 'Sélectionner' : 'Select'} />
                      </SelectTrigger>
                      <SelectContent>
                        {DRC_PROVINCES.map(p => (
                          <SelectItem key={p} value={p}>{p}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Capacité totale' : 'Total Capacity'}</Label>
                    <Input
                      type="number"
                      value={formData.capacity}
                      onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) || 0 })}
                    />
                    {totalCellCapacity > 0 && (
                      <p className="text-xs text-muted-foreground">
                        {language === 'fr' ? 'Capacité des cellules:' : 'Cells capacity:'} {totalCellCapacity}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Conditions de détention' : 'Detention Conditions'}</Label>
                    <Select value={formData.detention_conditions} onValueChange={(v) => setFormData({ ...formData, detention_conditions: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DETENTION_CONDITIONS.map(c => (
                          <SelectItem key={c.value} value={c.value}>
                            <div className="flex items-center gap-2">
                              <div className={`w-3 h-3 rounded-full ${c.color}`} />
                              {c.label[language] || c.label.fr}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Notes / Observations' : 'Notes / Observations'}</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder={language === 'fr' ? 'Notes sur la prison...' : 'Notes about the prison...'}
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Cells Tab */}
          <TabsContent value="cells" className="space-y-4 mt-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>{language === 'fr' ? 'Cellules & Pavillons' : 'Cells & Pavilions'}</CardTitle>
                  <CardDescription>
                    {language === 'fr' 
                      ? 'Définissez les cellules et pavillons de cette prison'
                      : 'Define cells and pavilions for this prison'}
                  </CardDescription>
                </div>
                <Button onClick={() => openCellModal()} className="gap-2">
                  <Plus className="w-4 h-4" />
                  {language === 'fr' ? 'Ajouter Cellule' : 'Add Cell'}
                </Button>
              </CardHeader>
              <CardContent>
                {formData.cells.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Grid3X3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>{language === 'fr' ? 'Aucune cellule définie' : 'No cells defined'}</p>
                    <Button variant="outline" className="mt-4" onClick={() => openCellModal()}>
                      <Plus className="w-4 h-4 mr-2" />
                      {language === 'fr' ? 'Ajouter la première cellule' : 'Add first cell'}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {/* Group by pavilion */}
                    {Object.entries(
                      formData.cells.reduce((acc, cell) => {
                        const pavilion = cell.pavilion || 'Sans pavillon';
                        if (!acc[pavilion]) acc[pavilion] = [];
                        acc[pavilion].push(cell);
                        return acc;
                      }, {})
                    ).map(([pavilion, cells]) => (
                      <div key={pavilion} className="border rounded-lg p-4">
                        <h4 className="font-semibold mb-3 text-primary">{pavilion}</h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                          {cells.map(cell => (
                            <div 
                              key={cell.cell_id} 
                              className={`p-3 border rounded-lg ${cell.is_active ? 'bg-background' : 'bg-muted opacity-60'}`}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-medium">{cell.name}</span>
                                <div className="flex gap-1">
                                  <Button variant="ghost" size="sm" onClick={() => openCellModal(cell)}>
                                    <Edit className="w-3 h-3" />
                                  </Button>
                                  <Button variant="ghost" size="sm" onClick={() => deleteCell(cell.cell_id)} className="text-destructive">
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>
                              <div className="text-sm text-muted-foreground space-y-1">
                                <div className="flex items-center gap-2">
                                  <Users className="w-3 h-3" />
                                  <span>{language === 'fr' ? 'Capacité:' : 'Capacity:'} {cell.capacity}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline" className="text-xs">
                                    {cell.cell_type === 'STANDARD' && (language === 'fr' ? 'Standard' : 'Standard')}
                                    {cell.cell_type === 'ISOLATION' && (language === 'fr' ? 'Isolement' : 'Isolation')}
                                    {cell.cell_type === 'VIP' && 'VIP'}
                                    {cell.cell_type === 'MEDICAL' && (language === 'fr' ? 'Médical' : 'Medical')}
                                    {cell.cell_type === 'WOMEN' && (language === 'fr' ? 'Femmes' : 'Women')}
                                    {cell.cell_type === 'MINORS' && (language === 'fr' ? 'Mineurs' : 'Minors')}
                                  </Badge>
                                  {cell.floor > 0 && (
                                    <span className="text-xs">Étage {cell.floor}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}

                    <div className="flex justify-between items-center pt-4 border-t mt-4">
                      <span className="text-muted-foreground">
                        {language === 'fr' ? 'Total:' : 'Total:'} {formData.cells.length} {language === 'fr' ? 'cellules' : 'cells'}
                      </span>
                      <span className="font-medium">
                        {language === 'fr' ? 'Capacité totale:' : 'Total capacity:'} {totalCellCapacity}
                      </span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Facilities Tab */}
          <TabsContent value="facilities" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <CardTitle>{language === 'fr' ? 'Installations & Équipements' : 'Facilities & Equipment'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                  {/* Infirmary */}
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <Stethoscope className={`w-6 h-6 ${formData.has_infirmary ? 'text-green-600' : 'text-muted-foreground'}`} />
                      <div>
                        <p className="font-medium">{language === 'fr' ? 'Infirmerie' : 'Infirmary'}</p>
                        <p className="text-xs text-muted-foreground">
                          {formData.has_infirmary 
                            ? (language === 'fr' ? 'Disponible' : 'Available')
                            : (language === 'fr' ? 'Non disponible' : 'Not available')}
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={formData.has_infirmary}
                      onCheckedChange={(v) => setFormData({ ...formData, has_infirmary: v })}
                    />
                  </div>

                  {/* Kitchen */}
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <UtensilsCrossed className={`w-6 h-6 ${formData.has_kitchen ? 'text-green-600' : 'text-muted-foreground'}`} />
                      <div>
                        <p className="font-medium">{language === 'fr' ? 'Cuisine' : 'Kitchen'}</p>
                        <p className="text-xs text-muted-foreground">
                          {formData.has_kitchen 
                            ? (language === 'fr' ? 'Disponible' : 'Available')
                            : (language === 'fr' ? 'Non disponible' : 'Not available')}
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={formData.has_kitchen}
                      onCheckedChange={(v) => setFormData({ ...formData, has_kitchen: v })}
                    />
                  </div>

                  {/* Toilets */}
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center gap-3 mb-3">
                      <Bath className="w-6 h-6 text-blue-600" />
                      <p className="font-medium">{language === 'fr' ? 'Toilettes' : 'Toilets'}</p>
                    </div>
                    <Input
                      type="number"
                      value={formData.toilets_count}
                      onChange={(e) => setFormData({ ...formData, toilets_count: parseInt(e.target.value) || 0 })}
                      min={0}
                    />
                  </div>
                </div>

                {/* Warning if conditions are poor */}
                {(formData.detention_conditions === 'POOR' || formData.detention_conditions === 'CRITICAL') && (
                  <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <AlertTriangle className="w-6 h-6 text-red-600" />
                    <div>
                      <p className="font-medium text-red-700 dark:text-red-400">
                        {language === 'fr' ? 'Attention: Conditions de détention critiques' : 'Warning: Critical detention conditions'}
                      </p>
                      <p className="text-sm text-red-600 dark:text-red-500">
                        {language === 'fr' 
                          ? 'Des améliorations urgentes sont nécessaires.'
                          : 'Urgent improvements are needed.'}
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Location Tab */}
          <TabsContent value="location" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <CardTitle>{language === 'fr' ? 'Localisation & Contact' : 'Location & Contact'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Adresse complète' : 'Full Address'}</Label>
                  <Textarea
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder={language === 'fr' ? 'Adresse de la prison...' : 'Prison address...'}
                    rows={2}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      {language === 'fr' ? 'Téléphone' : 'Phone'}
                    </Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+243 ..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      Email
                    </Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="prison@example.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      Latitude
                    </Label>
                    <Input
                      value={formData.latitude}
                      onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                      placeholder="-4.3217"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      Longitude
                    </Label>
                    <Input
                      value={formData.longitude}
                      onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                      placeholder="15.3125"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Cell Modal */}
        <Dialog open={showCellModal} onOpenChange={setShowCellModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingCell 
                  ? (language === 'fr' ? 'Modifier la Cellule' : 'Edit Cell')
                  : (language === 'fr' ? 'Nouvelle Cellule' : 'New Cell')}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Nom de la cellule' : 'Cell Name'} *</Label>
                  <Input
                    value={cellForm.name}
                    onChange={(e) => setCellForm({ ...cellForm, name: e.target.value })}
                    placeholder="Ex: A-101"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Pavillon' : 'Pavilion'}</Label>
                  <Input
                    value={cellForm.pavilion}
                    onChange={(e) => setCellForm({ ...cellForm, pavilion: e.target.value })}
                    placeholder="Ex: Pavillon A"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Capacité' : 'Capacity'}</Label>
                  <Input
                    type="number"
                    value={cellForm.capacity}
                    onChange={(e) => setCellForm({ ...cellForm, capacity: parseInt(e.target.value) || 0 })}
                    min={1}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Étage' : 'Floor'}</Label>
                  <Input
                    type="number"
                    value={cellForm.floor}
                    onChange={(e) => setCellForm({ ...cellForm, floor: parseInt(e.target.value) || 0 })}
                    min={0}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Type' : 'Type'}</Label>
                  <Select value={cellForm.cell_type} onValueChange={(v) => setCellForm({ ...cellForm, cell_type: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="STANDARD">{language === 'fr' ? 'Standard' : 'Standard'}</SelectItem>
                      <SelectItem value="ISOLATION">{language === 'fr' ? 'Isolement' : 'Isolation'}</SelectItem>
                      <SelectItem value="VIP">VIP</SelectItem>
                      <SelectItem value="MEDICAL">{language === 'fr' ? 'Médical' : 'Medical'}</SelectItem>
                      <SelectItem value="WOMEN">{language === 'fr' ? 'Femmes' : 'Women'}</SelectItem>
                      <SelectItem value="MINORS">{language === 'fr' ? 'Mineurs' : 'Minors'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  checked={cellForm.is_active}
                  onCheckedChange={(v) => setCellForm({ ...cellForm, is_active: v })}
                />
                <Label>{language === 'fr' ? 'Cellule active' : 'Cell active'}</Label>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCellModal(false)}>
                {language === 'fr' ? 'Annuler' : 'Cancel'}
              </Button>
              <Button onClick={saveCell}>
                <Save className="w-4 h-4 mr-2" />
                {language === 'fr' ? 'Enregistrer' : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default PrisonEditPage;
